import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  AlertTriangle,
  BookOpenCheck,
  ChevronLeft,
  ClipboardCheck,
  Clock,
  KeyRound,
  LoaderCircle,
  ShieldCheck,
  Sparkles,
  TimerOff,
  UserCheck,
  UsersRound,
} from "lucide-react";
import AssessmentNavigator from "./components/AssessmentNavigator";
import AssessmentReview from "./components/AssessmentReview";
import AssessmentQuestion from "./components/AssessmentQuestion";
import DrawingPad from "./components/DrawingPad";
import AssessmentOutcome from "./components/AssessmentOutcome";
import useDrawingAnswers from "./utils/useDrawingAnswers";
import studyDenHorizontal from "./assets/images/branding/Studyden_Horizontal.svg";
import {
  answerRevisionMap,
  answerText,
  applySaveResults,
  createServerAnswers,
  isAnswered,
  overallSaveState,
  pendingChanges,
  questionSaveState,
  serverEntry,
} from "./utils/answerSync";
import { auth, db } from "./firebaseConfig";
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  getDoc,
} from "firebase/firestore";
import { useLocation, useNavigate } from "react-router-dom";
import { DashboardHeader, DashboardSidebar } from "./Dashboard";
import {
  fetchStudentQuizAttemptStatus,
  fetchStudentQuizQuestions,
  saveStudentQuizAnswerChanges,
  startOrResumeStudentQuizAttempt,
  submitStudentQuizAnswersByRevision,
} from "./utils/studentAssessmentApi";
import "./EnterQuiz.css";
import "./EnterQuizAssessment.css";

const RETRY_SAVE_MS = 5000;
// Both outcome screens move on after this long (unchanged from before).
const OUTCOME_REDIRECT_SECONDS = 5;

const saveFailureMessage = (error) => {
  if (error?.code === "refresh-required") return error.message;
  if (error?.code === "attempt-expired") return "Time is up. Answers saved before the time limit were submitted.";
  if (error?.code === "assignment-closed") return "The submission deadline has passed.";
  return "Some answers could not be saved. We'll keep trying — your answers are still on this page.";
};

const studentInitials = (name = "") =>
  name.trim().split(/\s+/).filter(Boolean).slice(0, 2).map((part) => part[0].toUpperCase()).join("") || "S";

const positionKey = (sessionId) => `studyden.assessmentPosition.${sessionId}`;

const readStoredPosition = (sessionId, questionCount) => {
  try {
    const value = Number(window.sessionStorage.getItem(positionKey(sessionId)));
    return Number.isSafeInteger(value) && value >= 0 && value < questionCount ? value : 0;
  } catch (_error) {
    return 0;
  }
};

export const validateAssignmentRecord = (assignment = {}, userUid = "") => {
  if (
    assignment.studentUid !== userUid ||
    assignment.assessmentType !== "quiz" ||
    assignment.revokedAt != null ||
    typeof assignment.quizId !== "string" ||
    !assignment.quizId.trim() ||
    assignment.quizId.includes("/")
  ) {
    throw new Error("assignment-not-authorized");
  }
  return assignment.quizId.trim();
};

export const validateAssignmentQuizOwner = (assignment = {}, quizData = {}) => {
  if (!assignment.teacherUid || quizData.creatorUid !== assignment.teacherUid) {
    throw new Error("assignment-owner-mismatch");
  }
  return true;
};

export const hasValidAssignmentLearningContentHandoff = (
  locationState,
  assignmentId,
  currentRevision
) => {
  const access = locationState?.learningContentAccess;
  const handoffRevision = locationState?.learningContentRevision;
  return (
    access != null &&
    typeof access === "object" &&
    !Array.isArray(access) &&
    Object.keys(access).length === 2 &&
    access.type === "assignment" &&
    typeof access.assignmentId === "string" &&
    access.assignmentId === assignmentId &&
    Number.isSafeInteger(handoffRevision) &&
    handoffRevision >= 1 &&
    handoffRevision < Number.MAX_SAFE_INTEGER &&
    Number.isSafeInteger(currentRevision) &&
    currentRevision >= 1 &&
    currentRevision < Number.MAX_SAFE_INTEGER &&
    handoffRevision === currentRevision
  );
};

const isPlainObject = (value) => {
  if (value === null || typeof value !== "object" || Array.isArray(value)) return false;
  const prototype = Object.getPrototypeOf(value);
  return prototype === Object.prototype || prototype === null;
};

const hasControlCharacters = (value) =>
  Array.from(value).some((character) => {
    const code = character.charCodeAt(0);
    return code <= 31 || code === 127;
  });

export const quizCodeReturnContextFromSearch = (search = "") => {
  const params = new URLSearchParams(search);
  const teacherUid = params.get("teacherUid");
  const quizCode = params.get("quizCode");
  if (teacherUid == null && quizCode == null) return null;
  if (
    typeof teacherUid !== "string" ||
    !teacherUid ||
    teacherUid !== teacherUid.trim() ||
    teacherUid.includes("/") ||
    teacherUid.length > 128 ||
    typeof quizCode !== "string" ||
    !quizCode ||
    quizCode !== quizCode.trim() ||
    quizCode.length > 256 ||
    hasControlCharacters(teacherUid) ||
    hasControlCharacters(quizCode)
  ) {
    return null;
  }
  return { teacherUid, quizCode };
};

export const hasValidQuizCodeLearningContentHandoff = (
  locationState,
  returnContext,
  quizData = {}
) => {
  const access = locationState?.learningContentAccess;
  const handoffRevision = locationState?.learningContentRevision;
  const currentRevision = quizData.learningContentRevision;
  return (
    isPlainObject(access) &&
    Object.keys(access).length === 3 &&
    ["type", "teacherUid", "quizCode"].every((field) =>
      Object.prototype.hasOwnProperty.call(access, field)
    ) &&
    access.type === "quiz-code" &&
    typeof returnContext?.teacherUid === "string" &&
    typeof returnContext?.quizCode === "string" &&
    access.teacherUid === returnContext.teacherUid &&
    access.quizCode === returnContext.quizCode &&
    access.teacherUid === quizData.creatorUid &&
    access.quizCode === quizData.quizCode &&
    Number.isSafeInteger(handoffRevision) &&
    handoffRevision >= 1 &&
    handoffRevision < Number.MAX_SAFE_INTEGER &&
    Number.isSafeInteger(currentRevision) &&
    currentRevision >= 1 &&
    currentRevision < Number.MAX_SAFE_INTEGER &&
    handoffRevision === currentRevision
  );
};

const EnterQuiz = ({ user }) => {
  const [quizCode, setQuizCode] = useState("");
  const [quizQuestions, setQuizQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [savedQuestions, setSavedQuestions] = useState({});
  const [remainingTime, setRemainingTime] = useState(null);
  const [assignmentDueAtMs, setAssignmentDueAtMs] = useState(null);
  const [serverDeadlineClosed, setServerDeadlineClosed] = useState(false);
  const [nowMs, setNowMs] = useState(() => Date.now());
  const [quizLoaded, setQuizLoaded] = useState(false);
  const [quizLocked, setQuizLocked] = useState(false);
  const [submissionComplete, setSubmissionComplete] = useState(false);
  const [accessError, setAccessError] = useState(null);
  const [allowPaste, setAllowPaste] = useState(false);
  const [pasteBlockedId, setPasteBlockedId] = useState(null);
  const [redirectCountdown, setRedirectCountdown] = useState(OUTCOME_REDIRECT_SECONDS);
  // Set once by the first outcome (submitted or time up): the navigation
  // options its redirect uses. The redirect and the outcome button share
  // leaveOutcome, so the page navigates once.
  const [outcomeRedirect, setOutcomeRedirect] = useState(null);
  // What the server confirmed about the submission made when time ran out.
  const [expiryOutcome, setExpiryOutcome] = useState("pending");
  const [displayStudentName, setDisplayStudentName] = useState("Student");

  // Session management state
  const [sessionId, setSessionId] = useState(null);
  const [sessionLoaded, setSessionLoaded] = useState(false);
  const [alreadySubmitted, setAlreadySubmitted] = useState(false);
  const [terminalStatus, setTerminalStatus] = useState("submitted");
  // Why a finished attempt expired ("timer" or "assignment_deadline"), read
  // from the attempt itself so the closed screen names the right cause.
  const [terminalExpiryReason, setTerminalExpiryReason] = useState(null);
  const terminalSessionIdRef = useRef(null);
  const [resumedSession, setResumedSession] = useState(false);

  // Versioned answer saving: acknowledged server answers, requests in flight,
  // failures and conflicts are all kept per question at page level.
  const [serverAnswers, setServerAnswers] = useState({});
  const [inFlightSaves, setInFlightSaves] = useState({});
  const [failedSaves, setFailedSaves] = useState({});
  const [conflicts, setConflicts] = useState({});
  const [saveError, setSaveError] = useState(null);
  const [refreshRequired, setRefreshRequired] = useState(false);
  const [manualSavingId, setManualSavingId] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [view, setView] = useState("question");
  const [submitState, setSubmitState] = useState({ status: "idle" });
  const [quizTitle, setQuizTitle] = useState("");

  // ── Teacher selection state ──────────────────────────────────────
  // linkedTeachers: [{ uid, name, email, teacherCode }]
  const [linkedTeachers, setLinkedTeachers] = useState([]);
  const [selectedTeacherId, setSelectedTeacherId] = useState("");
  const [teachersLoading, setTeachersLoading] = useState(true);
  const [assignmentEntryLoading, setAssignmentEntryLoading] = useState(false);
  const [isJoining, setIsJoining] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [portalSearch, setPortalSearch] = useState("");

  // [THEME] Track dark mode from global source (localStorage)
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const savedMode = localStorage.getItem("darkMode");
    const mode = savedMode === "true" ? true : false;
    console.log("[THEME] EnterQuiz initial dark mode from localStorage:", mode);
    return mode;
  });

  const navigate = useNavigate();
  const location = useLocation();
  const assignmentId = new URLSearchParams(location.search).get("assignmentId")?.trim() || "";
  const deadlineClosed = serverDeadlineClosed ||
    (assignmentDueAtMs !== null && nowMs >= assignmentDueAtMs);
  const exitPath = assignmentId ? "/my-assessments" : "/dashboard";
  const timerStarted = useRef(false);
  const timerIntervalRef = useRef(null);
  const autosaveTimerRef = useRef(null);

  // ── Refs for values that must be readable inside closures/effects ──
  const sessionIdRef = useRef(null);
  const sessionLoadedRef = useRef(false);
  // Set once the quiz doc is resolved — used in submission writes so closures
  // always read the correct quizId even after state batching.
  const resolvedQuizIdRef = useRef(null);
  const resolvedTeacherUidRef = useRef(null);
  const resolvedQuizCodeRef = useRef(null);
  const deadlineFinalizedRef = useRef("");
  const assignmentLoadStartedRef = useRef("");
  const quizCodeReturnLoadStartedRef = useRef("");
  const attemptAccessRef = useRef(null);
  const answersRef = useRef({});
  const serverAnswersRef = useRef({});
  const conflictsRef = useRef({});
  const questionIdsRef = useRef([]);
  const saveLoopRef = useRef(null);
  const saveRequestedRef = useRef(false);
  const retryTimerRef = useRef(null);
  const refreshRequiredRef = useRef(false);
  const deadlineClosedRef = useRef(false);
  const submittingRef = useRef(false);
  // Set synchronously so a second click or Enter press is ignored before React
  // re-renders the disabled button.
  const joinInFlightRef = useRef(false);
  const questionHeadingRef = useRef(null);
  const focusQuestionRef = useRef(false);

  answersRef.current = answers;
  deadlineClosedRef.current = deadlineClosed;
  questionIdsRef.current = quizQuestions.map((question) => question.id);

  // Drawing answers (open-ended questions). The server decides whether the
  // feature is available; drawings that already exist are always shown.
  const drawings = useDrawingAnswers({
    uid: user?.uid,
    attemptId: sessionId,
    questionIds: questionIdsRef.current,
    active: quizLoaded && Boolean(sessionId) && !deadlineClosed && !alreadySubmitted && !submissionComplete,
    onAttemptEnded: (code) => {
      if (code === "attempt-expired") setRemainingTime(0);
      else if (code === "assignment-closed") setServerDeadlineClosed(true);
      else recoverSubmitOutcome(new Error("This assessment is no longer open."));
    },
  });
  const drawingsRef = useRef(drawings);
  drawingsRef.current = drawings;

  const setServerAnswersBoth = (next) => {
    serverAnswersRef.current = next;
    setServerAnswers(next);
  };
  const setConflictsBoth = (next) => {
    conflictsRef.current = next;
    setConflicts(next);
  };

  // Helper: set sessionId in BOTH state and ref at the same time
  const setSessionIdBoth = (id) => {
    console.log("[SESSION] setSessionIdBoth →", id);
    sessionIdRef.current = id;
    setSessionId(id);
  };

  // Helper: set sessionLoaded in BOTH state and ref at the same time
  const setSessionLoadedBoth = (val) => {
    console.log("[SESSION] setSessionLoadedBoth →", val);
    sessionLoadedRef.current = val;
    setSessionLoaded(val);
  };

  useEffect(() => {
    console.log("User in EnterQuiz component:", user);
  }, [user]);

  // [THEME] Listen for dark mode changes from localStorage (synced from Dashboard)
  useEffect(() => {
    const handleStorageChange = () => {
      const savedMode = localStorage.getItem("darkMode");
      const newMode = savedMode === "true";
      console.log("[ENTER QUIZ THEME DEBUG] localStorage darkMode:", savedMode);
      console.log("[ENTER QUIZ THEME DEBUG] parsed boolean:", newMode);
      setIsDarkMode(newMode);

      document.body.classList.toggle("dark-mode", newMode);
      document.documentElement.setAttribute("data-dark-mode", newMode ? "true" : "false");
      console.log("[ENTER QUIZ THEME DEBUG] body has dark-mode:", document.body.classList.contains("dark-mode"));
      console.log("[ENTER QUIZ THEME DEBUG] body className:", document.body.className);
      console.log("[ENTER QUIZ THEME DEBUG] documentElement className:", document.documentElement.className);
      console.log("[ENTER QUIZ THEME DEBUG] data-dark-mode attribute:", document.documentElement.getAttribute("data-dark-mode"));
    };

    window.addEventListener("storage", handleStorageChange);

    const savedMode = localStorage.getItem("darkMode");
    const currentMode = savedMode === "true";
    console.log("═══════════════════════════════════════════════════");
    console.log("[ENTER QUIZ THEME DEBUG] INITIAL MOUNT");
    console.log("[ENTER QUIZ THEME DEBUG] localStorage darkMode:", savedMode);
    console.log("[ENTER QUIZ THEME DEBUG] parsed boolean:", currentMode);
    document.body.classList.toggle("dark-mode", currentMode);
    document.documentElement.setAttribute("data-dark-mode", currentMode ? "true" : "false");
    console.log("[ENTER QUIZ THEME DEBUG] body has dark-mode:", document.body.classList.contains("dark-mode"));
    console.log("[ENTER QUIZ THEME DEBUG] body className:", document.body.className);
    console.log("[ENTER QUIZ THEME DEBUG] documentElement className:", document.documentElement.className);
    console.log("[ENTER QUIZ THEME DEBUG] data-dark-mode attribute:", document.documentElement.getAttribute("data-dark-mode"));
    console.log("═══════════════════════════════════════════════════");

    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
  }, []);

  // Fetch the Firestore profile so manual-signup usernames display correctly
  useEffect(() => {
    const fetchDisplayName = async () => {
      if (!user?.uid) return;
      try {
        const userSnap = await getDoc(doc(db, "users", user.uid));
        if (userSnap.exists()) {
          const profile = userSnap.data();
          const name =
            profile.username ||
            profile.displayName ||
            user.displayName ||
            user.email?.split("@")[0] ||
            "Student";
          setDisplayStudentName(name);
          return;
        }
      } catch (err) {
        console.error("Error fetching user profile for display name:", err);
      }
      setDisplayStudentName(
        user.displayName || user.email?.split("@")[0] || "Student"
      );
    };
    fetchDisplayName();
  }, [user?.uid, user?.displayName, user?.email]);

  // ── Load linked teachers ─────────────────────────────────────────
  // Reads student's teacherCodes, then finds matching teacher accounts.
  useEffect(() => {
    const loadLinkedTeachers = async () => {
      if (assignmentId) {
        setTeachersLoading(false);
        return;
      }
      if (!user?.uid) {
        setTeachersLoading(false);
        return;
      }
      setTeachersLoading(true);
      try {
        console.log("[ENTER QUIZ] Student UID:", user.uid);

        const userSnap = await getDoc(doc(db, "users", user.uid));
        if (!userSnap.exists()) {
          console.log("[ENTER QUIZ] Student user doc not found");
          setTeachersLoading(false);
          return;
        }

        const userData = userSnap.data();
        const teacherCodes = userData.teacherCodes || [];
        console.log("[ENTER QUIZ] Student teacherCodes:", teacherCodes);

        if (teacherCodes.length === 0) {
          setLinkedTeachers([]);
          console.log("[ENTER QUIZ] Linked teachers loaded: 0");
          setTeachersLoading(false);
          return;
        }

        // Query users where teacherCode is in the student's teacherCodes.
        // Chunk in groups of 10 (Firestore 'in' limit).
        const teachers = [];
        for (let i = 0; i < teacherCodes.length; i += 10) {
          const chunk = teacherCodes.slice(i, i + 10);
          const usersRef = collection(db, "users");
          const teacherQuery = query(
            usersRef,
            where("teacherCode", "in", chunk)
          );
          const teacherSnap = await getDocs(teacherQuery);
          teacherSnap.docs.forEach((d) => {
            const data = d.data();
            teachers.push({
              uid: d.id,
              name: data.username || data.displayName || null,
              email: data.email || null,
              teacherCode: data.teacherCode || "",
            });
          });
        }

        console.log("[ENTER QUIZ] Linked teachers loaded:", teachers.length);
        setLinkedTeachers(teachers);

        // Auto-select if only one teacher linked
        if (teachers.length === 1) {
          setSelectedTeacherId(teachers[0].uid);
          console.log("[ENTER QUIZ] Auto-selected teacher UID:", teachers[0].uid);
        }
      } catch (err) {
        console.error("[ENTER QUIZ] Error loading linked teachers:", err);
      } finally {
        setTeachersLoading(false);
      }
    };
    loadLinkedTeachers();
  }, [assignmentId, user?.uid]);

  // ==================== Session autosave ====================
  // One save request at a time. Each request sends only the questions whose
  // local value differs from the acknowledged server value, each against the
  // revision last acknowledged, so a delayed or duplicated request can never
  // overwrite newer work. Edits made while a request is in flight are sent by
  // the next pass of the loop.
  const runSave = useCallback(() => {
    if (saveLoopRef.current) {
      saveRequestedRef.current = true;
      return saveLoopRef.current;
    }
    const loop = (async () => {
      do {
        saveRequestedRef.current = false;
        const sid = sessionIdRef.current;
        if (!sid || !sessionLoadedRef.current || deadlineClosedRef.current || refreshRequiredRef.current) return;
        const changes = pendingChanges(
          answersRef.current, serverAnswersRef.current, questionIdsRef.current, conflictsRef.current
        );
        const changedIds = Object.keys(changes);
        if (changedIds.length === 0) return;
        setInFlightSaves(Object.fromEntries(changedIds.map((questionId) => [questionId, changes[questionId].text])));
        try {
          const { results } = await saveStudentQuizAnswerChanges(sid, changes);
          if (sessionIdRef.current !== sid) return;
          const applied = applySaveResults(serverAnswersRef.current, changes, results);
          setServerAnswersBoth(applied.server);
          if (Object.keys(applied.conflicts).length > 0) {
            setConflictsBoth({ ...conflictsRef.current, ...applied.conflicts });
          }
          setFailedSaves((previous) => {
            const next = { ...previous };
            changedIds.forEach((questionId) => delete next[questionId]);
            return next;
          });
          setSaveError(null);
        } catch (err) {
          console.error("[AUTOSAVE] FAILED:", err?.code || err?.message);
          if (err?.code === "attempt-expired") setRemainingTime(0);
          if (err?.code === "assignment-closed") setServerDeadlineClosed(true);
          if (err?.code === "refresh-required") {
            refreshRequiredRef.current = true;
            setRefreshRequired(true);
          }
          setFailedSaves((previous) => ({
            ...previous,
            ...Object.fromEntries(changedIds.map((questionId) => [questionId, true])),
          }));
          setSaveError(saveFailureMessage(err));
          clearTimeout(retryTimerRef.current);
          if (!["attempt-expired", "assignment-closed", "refresh-required"].includes(err?.code)) {
            retryTimerRef.current = setTimeout(() => { runSave(); }, RETRY_SAVE_MS);
          }
          return;
        } finally {
          setInFlightSaves({});
        }
      } while (saveRequestedRef.current);
    })();
    saveLoopRef.current = loop.finally(() => {
      saveLoopRef.current = null;
      // A request that arrived just as the loop finished is not dropped.
      if (saveRequestedRef.current) runSave();
    });
    return saveLoopRef.current;
  }, []);

  // Saves everything outstanding and reports the questions still not saved
  // (failed, or held back by a conflict).
  const flushSaves = useCallback(async () => {
    clearTimeout(autosaveTimerRef.current);
    for (let pass = 0; pass < 3; pass += 1) {
      await runSave();
      const remaining = pendingChanges(
        answersRef.current, serverAnswersRef.current, questionIdsRef.current, conflictsRef.current
      );
      if (Object.keys(remaining).length === 0) break;
    }
    return questionIdsRef.current.filter((questionId) => questionSaveState(questionId, {
      local: answersRef.current,
      server: serverAnswersRef.current,
      conflicts: conflictsRef.current,
    }) !== "saved");
  }, [runSave]);

  // Debounced: fires 1 second after the student stops typing.
  useEffect(() => {
    if (deadlineClosed || !sessionLoadedRef.current || !sessionIdRef.current) return undefined;
    if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current);
    autosaveTimerRef.current = setTimeout(() => { runSave(); }, 1000);
    return () => clearTimeout(autosaveTimerRef.current);
  }, [answers, conflicts, deadlineClosed, runSave]);

  useEffect(() => () => clearTimeout(retryTimerRef.current), []);

  // ==========================================================

  const loadQuiz = async (options = null) => {
    const assignmentResolution = options?.quizDoc ? options : null;
    const quizCodeReturnContext = !assignmentResolution ? options?.quizCodeReturnContext : null;
    const activeQuizCode =
      assignmentResolution?.quizCode || quizCodeReturnContext?.quizCode || quizCode;
    const activeTeacherId = quizCodeReturnContext?.teacherUid || selectedTeacherId;
    const isQuizCodeJoin = !assignmentResolution;

    if (isQuizCodeJoin && joinInFlightRef.current) return;

    if (!activeQuizCode) {
      alert("Please enter a Test Code!");
      return;
    }

    // ── Require teacher selection ──────────────────────────────────
    if (!assignmentResolution && !activeTeacherId) {
      setAccessError("Please select a teacher before loading a test.");
      return;
    }

    if (isQuizCodeJoin) {
      joinInFlightRef.current = true;
      setIsJoining(true);
    }
    // Stay pending while the route changes so the button cannot re-enable mid-navigation.
    let leavingForLearningContent = false;
    setAccessError(null);

    try {
      // --- 1. Fetch the quiz scoped to the selected teacher ---
      console.log("[ENTER QUIZ] Entered quiz code:", activeQuizCode);
      console.log("[ENTER QUIZ] Selected teacher UID:", activeTeacherId);
      console.log("[SESSION] Step 1: Fetching quiz with code:", activeQuizCode, "for teacher:", activeTeacherId);

      let quizData;
      let quizId;
      if (assignmentResolution) {
        ({ quizData, quizId } = assignmentResolution);
      } else {
        const quizzesRef = collection(db, "quizzes");
        const quizQuery = query(
          quizzesRef,
          where("quizCode", "==", activeQuizCode),
          where("creatorUid", "==", activeTeacherId)
        );
        const quizSnapshot = await getDocs(quizQuery);

        console.log("[ENTER QUIZ] Matching quizzes for selected teacher:", quizSnapshot.size);

        if (quizSnapshot.empty) {
          setAccessError(
            "No test with that code was found for the selected teacher."
          );
          return;
        }

        if (quizSnapshot.size > 1) {
          setAccessError(
            "More than one test with this code exists for the selected teacher. " +
              "Please ask your teacher to rename or archive the duplicate."
          );
          return;
        }

        const matchedQuizDoc = quizSnapshot.docs[0];
        quizData = matchedQuizDoc.data();
        quizId = matchedQuizDoc.id;
      }

      // Store resolved IDs in refs so all submission callbacks can read them
      // without stale-closure issues.
      resolvedQuizIdRef.current = quizId;
      resolvedTeacherUidRef.current = quizData.creatorUid || activeTeacherId;
      resolvedQuizCodeRef.current = activeQuizCode;

      console.log("[ENTER QUIZ] Selected quiz ID:", quizId);
      console.log(
        "[SESSION] Quiz found:", quizId,
        "| locked:", quizData.locked,
        "| questionIds:", (quizData.questionIds || []).length
      );

      // --- 2. Fetch student profile (needed for role, extra time, teacher code) ---
      console.log("[SESSION] Step 2: Fetching user profile for UID:", user.uid);
      const userDocRef = doc(db, "users", user.uid);
      const userDocSnap = await getDoc(userDocRef);
      const userData = userDocSnap.exists() ? userDocSnap.data() : {};
      const userRole = userData.role || "student";
      console.log("[SESSION] User role:", userRole);

      // --- 3. Teacher link validation (applies to all students) ---
      if (!assignmentResolution && userRole !== "teacher" && userRole !== "admin") {
        console.log("[SESSION] Step 3: Validating teacher link...");
        const creatorUid = quizData.creatorUid;
        if (!creatorUid) {
          setAccessError("This test is missing teacher information. Please contact your teacher.");
          return;
        }
        const teacherDocSnap = await getDoc(doc(db, "users", creatorUid));
        if (!teacherDocSnap.exists()) {
          setAccessError("This test is missing teacher information. Please contact your teacher.");
          return;
        }
        const teacherCode = teacherDocSnap.data().teacherCode;
        if (!teacherCode) {
          setAccessError("This test is missing teacher information. Please contact your teacher.");
          return;
        }
        const studentTeacherCodes = userData.teacherCodes || [];
        if (!studentTeacherCodes.includes(teacherCode)) {
          setAccessError("You must join this teacher before accessing this test.");
          return;
        }
        console.log("[SESSION] Teacher link validated ✓");
      } else {
        console.log("[SESSION] Step 3: Skipped teacher link check (user is", userRole, ")");
      }

      const attemptAccess = assignmentResolution
        ? { type: "assignment", assignmentId }
        : {
            type: "quiz-code",
            teacherUid: quizData.creatorUid || activeTeacherId,
            quizCode: activeQuizCode,
          };

      // --- Helper: fetch quiz questions in order ---
      const loadQuestions = async () => {
        const delivered = await fetchStudentQuizQuestions(attemptAccess);
        if (delivered.quiz.id !== quizId) {
          throw new Error("The delivered assessment does not match the selected test.");
        }
        return delivered.questions;
      };

      console.log("[SESSION] Requesting authoritative start/resume boundary...");
      attemptAccessRef.current = attemptAccess;
      const attempt = await startOrResumeStudentQuizAttempt(attemptAccess, quizId, { versioned: true });
      if (attempt.quizId !== quizId) {
        throw new Error("The attempt does not match the selected test.");
      }
      if (attempt.mode === "terminal" || attempt.mode === "awaiting-result") {
        terminalSessionIdRef.current = attempt.session?.id || null;
        setTerminalStatus(attempt.session?.status === "expired" ? "expired" : "submitted");
        setAlreadySubmitted(true);
        return;
      }

      const session = attempt.session;
      const expiresAtMs = session.expiresAt?.getTime() ?? null;
      const remainingSec = expiresAtMs === null
        ? null
        : Math.max(0, Math.ceil((expiresAtMs - Date.now()) / 1000));
      setSessionIdBoth(session.id);
      setAnswers(session.answers || {});
      setServerAnswersBoth(createServerAnswers(session.answers || {}, session.answerRevisions || {}));
      setConflictsBoth({});
      setRemainingTime(remainingSec);
      setResumedSession(attempt.mode === "resumed");
      console.log("[SESSION] Step 7: Loading questions...");
      const fetchedQuestions = await loadQuestions();
      console.log("[SESSION] Loaded", fetchedQuestions.length, "questions ✓");
      setQuizQuestions(fetchedQuestions);
      setCurrentIndex(readStoredPosition(session.id, fetchedQuestions.length));
      setQuizTitle(typeof quizData.quizTitle === "string" && quizData.quizTitle.trim()
        ? quizData.quizTitle.trim()
        : "Assessment");
      setAllowPaste(quizData.allowPaste === true);

      setSessionLoadedBoth(true);
      setQuizLoaded(true);
      if (attempt.mode === "resumed" && remainingSec === 0) {
        handleExpiredSession(session.id, fetchedQuestions);
      }
      console.log("[SESSION] Quiz loaded successfully ✓ (sessionId:", sessionIdRef.current, ")");
    } catch (error) {
      console.error("[QUIZ SESSION ERROR]", error);
      if (error?.code) console.error("[QUIZ SESSION ERROR CODE]", error.code);
      if (error?.message) console.error("[QUIZ SESSION ERROR MESSAGE]", error.message);
      if (error?.code === "learning-content-required") {
        leavingForLearningContent = true;
        if (assignmentResolution) {
          navigate(`/learning-content?assignmentId=${encodeURIComponent(assignmentId)}`, {
            replace: true,
          });
        } else {
          navigate("/learning-content", {
            replace: true,
            state: { learningContentAccess: {
              type: "quiz-code",
              teacherUid: resolvedTeacherUidRef.current || activeTeacherId,
              quizCode: activeQuizCode,
            } },
          });
        }
      } else if (error?.code === "assignment-closed") {
        setServerDeadlineClosed(true);
      } else if (error?.code === "quiz-locked") {
        setQuizLocked(true);
      } else if (assignmentResolution) {
        setAccessError("We could not open this assigned assessment. Please return to My Work and try again.");
      } else {
        alert(`Failed to load test: ${error?.message || "Unknown error"}`);
      }
    } finally {
      if (isQuizCodeJoin && !leavingForLearningContent) {
        joinInFlightRef.current = false;
        setIsJoining(false);
      }
    }
  };

  useEffect(() => {
    if (assignmentId || !user?.uid) return;
    const returnContext = quizCodeReturnContextFromSearch(location.search);
    if (!returnContext) return;
    const returnKey = `${returnContext.teacherUid}\u0000${returnContext.quizCode}`;
    if (quizCodeReturnLoadStartedRef.current === returnKey) return;
    quizCodeReturnLoadStartedRef.current = returnKey;
    setSelectedTeacherId(returnContext.teacherUid);
    setQuizCode(returnContext.quizCode);
    loadQuiz({ quizCodeReturnContext: returnContext });
    // `loadQuiz` remains the single secure quiz/session runner. The guard ref
    // prevents render-only changes from starting the return flow twice.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assignmentId, location.search, user?.uid]);

  useEffect(() => {
    if (!assignmentId || !user?.uid || assignmentLoadStartedRef.current === assignmentId) {
      return;
    }
    assignmentLoadStartedRef.current = assignmentId;

    const loadAssignedQuiz = async () => {
      setAssignmentEntryLoading(true);
      setAccessError(null);
      try {
        if (assignmentId.length > 256 || assignmentId.includes("/")) {
          throw new Error("invalid-assignment-id");
        }

        const assignmentSnap = await getDoc(doc(db, "quizAssignments", assignmentId));
        if (!assignmentSnap.exists()) throw new Error("assignment-not-found");
        const assignment = assignmentSnap.data() || {};
        setAssignmentDueAtMs(assignment.dueAt?.toMillis?.() ?? null);
        const quizId = validateAssignmentRecord(assignment, user.uid);
        const quizSnap = await getDoc(doc(db, "quizzes", quizId));
        if (!quizSnap.exists()) throw new Error("quiz-not-found");
        const quizData = quizSnap.data() || {};
        validateAssignmentQuizOwner(assignment, quizData);
        const currentQuizCode = typeof quizData.quizCode === "string"
          ? quizData.quizCode.trim()
          : "";
        if (!currentQuizCode) throw new Error("quiz-code-missing");

        setQuizCode(currentQuizCode);
        setSelectedTeacherId(assignment.teacherUid);
        await loadQuiz({
          quizDoc: quizSnap,
          quizData,
          quizId,
          quizCode: currentQuizCode,
        });
      } catch (error) {
        console.error("[ENTER QUIZ] Assigned assessment access failed", error);
        setAccessError(
          error?.message === "quiz-not-found"
            ? "This assessment is currently unavailable. Please contact your teacher."
            : "We could not verify this assigned assessment. Return to My Work and try again."
        );
      } finally {
        setAssignmentEntryLoading(false);
      }
    };

    loadAssignedQuiz();
    // `loadQuiz` intentionally remains the existing shared runner. The guard ref
    // prevents a new assignment attempt from being created by render-only changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assignmentId, user?.uid]);

  // Track authorised deadline edits while this page stays open. The backend
  // remains authoritative for every save and submission.
  useEffect(() => {
    if (!assignmentId || !user?.uid) return undefined;
    let active = true;
    const refreshDeadline = async () => {
      try {
        const snapshot = await getDoc(doc(db, "quizAssignments", assignmentId));
        if (!active || !snapshot.exists()) return;
        const assignment = snapshot.data() || {};
        if (assignment.studentUid !== user.uid || assignment.revokedAt != null) return;
        const latestDueAtMs = assignment.dueAt?.toMillis?.() ?? null;
        setAssignmentDueAtMs(latestDueAtMs);
        if (latestDueAtMs === null || latestDueAtMs > Date.now()) setServerDeadlineClosed(false);
      } catch (error) {
        console.error("[ENTER QUIZ] Could not refresh assignment deadline", error);
      }
    };
    const refreshInterval = window.setInterval(refreshDeadline, 5000);
    const clockInterval = window.setInterval(() => setNowMs(Date.now()), 1000);
    const onVisibility = () => { if (document.visibilityState === "visible") refreshDeadline(); };
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      active = false;
      window.clearInterval(refreshInterval);
      window.clearInterval(clockInterval);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [assignmentId, user?.uid]);

  useEffect(() => {
    if (!deadlineClosed || !quizLoaded || !sessionId || deadlineFinalizedRef.current === sessionId) return;
    deadlineFinalizedRef.current = sessionId;
    // Expiry finalizes the answers the server already holds; revisions only
    // identify them, so nothing typed after the cutoff can be accepted.
    submitStudentQuizAnswersByRevision(
      sessionId, answerRevisionMap(serverAnswersRef.current, questionIdsRef.current), "expired"
    ).catch((error) => {
      console.error("[ENTER QUIZ] Could not finalize closed assessment", error);
    });
  }, [deadlineClosed, quizLoaded, sessionId]);

  // Handle a session that has expired on resume — finalize the saved answers.
  const handleExpiredSession = useCallback(
    async (expiredSessionId, questions) => {
      try {
        console.log("[SESSION] Session expired on resume — finalizing saved answers");
        await submitStudentQuizAnswersByRevision(
          expiredSessionId,
          answerRevisionMap(serverAnswersRef.current, questions.map((question) => question.id)),
          "expired"
        );
        console.log("[SESSION] Expired session finalized ✓");
      } catch (err) {
        console.error("[SESSION] Error finalizing expired session:", err);
      }
    },
    []
  );

  // Edits go straight into page-level state, whichever question is showing.
  const handleAnswerChange = useCallback((questionId, value) => {
    if (deadlineClosed) return;
    setAnswers((prev) => ({ ...prev, [questionId]: value }));
  }, [deadlineClosed]);

  // Per-question Save: persists to the server first, and only then shows
  // "✓ Saved" and locks the answer (Edit Answer unlocks it, as before).
  const saveAnswer = useCallback(
    async (questionId) => {
      if (!user?.uid) {
        alert("User is not logged in. Please log in and try again.");
        return;
      }
      setManualSavingId(questionId);
      try {
        const unsaved = await flushSaves();
        if (!unsaved.includes(questionId)) {
          setSavedQuestions((prev) => ({ ...prev, [questionId]: true }));
        }
      } finally {
        setManualSavingId(null);
      }
    },
    [user, flushSaves]
  );

  const resolveConflict = useCallback((questionId, keep) => {
    const { [questionId]: serverText, ...rest } = conflictsRef.current;
    if (keep === "saved") setAnswers((prev) => ({ ...prev, [questionId]: answerText(serverText) }));
    setConflictsBoth(rest);
  }, []);

  const goToQuestion = useCallback((index) => {
    if (index < 0 || index >= questionIdsRef.current.length) return;
    focusQuestionRef.current = true;
    setView("question");
    setCurrentIndex(index);
  }, []);

  // Remember the position for a refresh in this tab; move focus to the new
  // question's heading after navigation.
  useEffect(() => {
    if (!sessionId) return;
    try { window.sessionStorage.setItem(positionKey(sessionId), String(currentIndex)); } catch (_error) { /* storage unavailable */ }
  }, [sessionId, currentIndex]);

  useEffect(() => {
    if (focusQuestionRef.current && view === "question") {
      focusQuestionRef.current = false;
      questionHeadingRef.current?.focus();
    }
  }, [currentIndex, view]);

  const formatTime = useCallback((seconds) => {
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes.toString().padStart(2, "0")}:${secs
      .toString()
      .padStart(2, "0")}`;
  }, []);

  // Timer countdown
  useEffect(() => {
    if (quizLoaded && remainingTime !== null && remainingTime > 0 && !timerStarted.current) {
      timerStarted.current = true;
      timerIntervalRef.current = setInterval(() => {
        setRemainingTime((prev) => {
          if (prev <= 1) {
            clearInterval(timerIntervalRef.current);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(timerIntervalRef.current);
    }
  }, [quizLoaded]); // eslint-disable-line react-hooks/exhaustive-deps

  // beforeunload — store answers the server has not acknowledged in
  // localStorage as a backup, with the revision each one was based on.
  useEffect(() => {
    if (quizLoaded && quizQuestions.length > 0) {
      const handleBeforeUnload = (event) => {
        event.preventDefault();
        event.returnValue = "";
        const unsavedQuestions = quizQuestions
          .filter((question) => answerText(answersRef.current[question.id]) !==
            serverEntry(serverAnswersRef.current, question.id).text)
          .map((question) => ({
            sessionId: sessionIdRef.current,
            questionId: question.id,
            answer: answerText(answersRef.current[question.id]),
            baseRevision: serverEntry(serverAnswersRef.current, question.id).revision,
          }));
        if (unsavedQuestions.length > 0) {
          try {
            localStorage.setItem(
              `quiz_${quizCode}_${user?.uid}_pending_saves`,
              JSON.stringify(unsavedQuestions)
            );
          } catch (error) {
            console.error("Error storing pending saves:", error);
          }
        }
      };
      window.addEventListener("beforeunload", handleBeforeUnload);
      return () => window.removeEventListener("beforeunload", handleBeforeUnload);
    }
  }, [quizLoaded, quizQuestions, quizCode, user]);

  // Process any pending saves from localStorage on quiz load. A backup is
  // restored only into the same attempt and only while the server still holds
  // the revision it was based on, so an old backup can never replace newer
  // work saved from another device. Older backups without a revision only
  // fill answers that are still blank on the server.
  useEffect(() => {
    const processPendingSaves = async () => {
      if (!user?.uid || !quizCode || !quizLoaded) return;
      const pendingSavesKey = `quiz_${quizCode}_${user.uid}_pending_saves`;
      const pendingSavesJson = localStorage.getItem(pendingSavesKey);
      if (pendingSavesJson) {
        try {
          const pendingSaves = JSON.parse(pendingSavesJson);
          setAnswers((prev) => {
            const updated = { ...prev };
            (Array.isArray(pendingSaves) ? pendingSaves : []).forEach((item) => {
              if (!item?.questionId || typeof item.answer !== "string" || !item.answer) return;
              const entry = serverEntry(serverAnswersRef.current, item.questionId);
              const restorable = Number.isSafeInteger(item.baseRevision)
                ? item.sessionId === sessionIdRef.current && item.baseRevision === entry.revision
                : entry.text === "";
              if (restorable) updated[item.questionId] = item.answer;
            });
            return updated;
          });
          localStorage.removeItem(pendingSavesKey);
          console.log("[SESSION] Restored", pendingSaves.length, "pending saves from localStorage");
        } catch (error) {
          console.error("Error processing pending saves:", error);
        }
      }
    };
    processPendingSaves();
  }, [quizLoaded, quizCode, user]);

  // Timer expiration auto-submit
  useEffect(() => {
    if (remainingTime === 0 && quizQuestions.length > 0 && quizLoaded) {
      const currentSessionId = sessionIdRef.current;

      const autoSubmitOnExpiry = async () => {
        setExpiryOutcome("pending");
        try {
          if (currentSessionId) {
            // The server refuses saves after its own cutoff, so this only
            // keeps edits that genuinely arrive in time. An open drawing gets
            // one last checkpoint; the server then submits its latest draft.
            await Promise.all([flushSaves().catch(() => {}), drawingsRef.current.flushForExpiry()]);
            const result = await submitStudentQuizAnswersByRevision(
              currentSessionId,
              answerRevisionMap(serverAnswersRef.current, questionIdsRef.current),
              "expired"
            );
            console.log("[SESSION] Marked session as expired on timeout");
            setExpiryOutcome(result?.status === "submitted" ? "already-submitted" : "expired");
          } else {
            setExpiryOutcome("unconfirmed");
          }
        } catch (error) {
          console.error("Error auto-saving on timer expiry:", error);
          // Never shown as submitted: the server reconciles the saved answers.
          setExpiryOutcome("unconfirmed");
        }
      };

      autoSubmitOnExpiry();
      setOutcomeRedirect((current) => current || { options: { replace: true } });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [remainingTime, quizLoaded]);

  useEffect(() => {
    const attemptId = sessionIdRef.current || terminalSessionIdRef.current;
    if (terminalStatus !== "expired" || !attemptId) return undefined;
    let active = true;
    getDoc(doc(db, "quizSessions", attemptId))
      .then((snapshot) => {
        if (active && snapshot?.exists?.()) setTerminalExpiryReason(snapshot.data()?.expiryReason || null);
      })
      .catch(() => { /* the timer wording remains */ });
    return () => { active = false; };
  }, [terminalStatus]);

  // A finished attempt: submitted shows the completion screen and redirects;
  // expired shows the existing "Time is up" status.
  const finishAttempt = useCallback((status) => {
    if (timerIntervalRef.current) clearInterval(timerIntervalRef.current);
    if (status === "expired") {
      setTerminalStatus("expired");
      setAlreadySubmitted(true);
      return;
    }
    setSubmissionComplete(true);
    setOutcomeRedirect((current) => current || { options: null });
  }, []);

  const outcomeLeftRef = useRef(false);
  const leaveOutcome = useCallback((options) => {
    if (outcomeLeftRef.current) return;
    outcomeLeftRef.current = true;
    if (options) navigate(exitPath, options);
    else navigate(exitPath);
  }, [navigate, exitPath]);
  const leaveOutcomeRef = useRef(leaveOutcome);
  leaveOutcomeRef.current = leaveOutcome;

  // The outcome screens' countdown and redirect, cleared if the page closes.
  useEffect(() => {
    if (!outcomeRedirect) return undefined;
    const countdown = setInterval(() => setRedirectCountdown((prev) => Math.max(0, prev - 1)), 1000);
    const redirect = setTimeout(() => leaveOutcomeRef.current(outcomeRedirect.options), OUTCOME_REDIRECT_SECONDS * 1000);
    return () => {
      clearInterval(countdown);
      clearTimeout(redirect);
    };
  }, [outcomeRedirect]);

  // After an uncertain submit (network failure, server error), ask the server
  // what actually happened instead of guessing.
  const recoverSubmitOutcome = useCallback(async (error) => {
    try {
      const state = await fetchStudentQuizAttemptStatus(
        attemptAccessRef.current, resolvedQuizIdRef.current, { versioned: true }
      );
      if (state.mode === "terminal" || state.mode === "awaiting-result") {
        finishAttempt(state.session?.status === "expired" ? "expired" : "submitted");
        return;
      }
      setSubmitState({
        status: "error",
        message: `${error?.message || "Your assessment could not be submitted."} Your answers are still saved. Please try again.`,
      });
    } catch (statusError) {
      if (statusError?.code === "assignment-closed") {
        setServerDeadlineClosed(true);
        return;
      }
      setSubmitState({
        status: "error",
        message: "We couldn't confirm whether your assessment was submitted. Check your connection and try again — retrying is safe and won't submit twice.",
      });
    }
  }, [finishAttempt]);

  // Final submission from the review overview. Outstanding edits are saved
  // first; if any cannot be saved the student chooses to retry or to submit
  // the answers already saved. The server finalizes its own saved answers.
  const submitAssessment = useCallback(async ({ savedOnly = false } = {}) => {
    if (deadlineClosedRef.current || submittingRef.current) return;
    const currentSessionId = sessionIdRef.current;
    if (!currentSessionId) return;
    if (drawingsRef.current.openDraftIds.length > 0) {
      setSubmitState({ status: "drawing-drafts" });
      return;
    }
    submittingRef.current = true;
    setSubmitState({ status: "submitting" });
    try {
      if (savedOnly) {
        if (saveLoopRef.current) await saveLoopRef.current;
      } else {
        const unsaved = await flushSaves();
        if (unsaved.length > 0) {
          setSubmitState({ status: "unsaved", unsavedIds: unsaved });
          return;
        }
      }
      const result = await submitStudentQuizAnswersByRevision(
        currentSessionId,
        answerRevisionMap(serverAnswersRef.current, questionIdsRef.current),
        "submitted",
        ...(drawingsRef.current.loaded ? [{ drawingRevisions: drawingsRef.current.drawingRevisions() }] : [])
      );
      console.log(`[SESSION] Session ${currentSessionId} marked as ${result.status}`);
      setSubmitState({ status: "idle" });
      finishAttempt(result.status);
    } catch (error) {
      console.error("Error during submission:", error?.code || error?.message);
      if (error?.code === "answers-changed" && error.details) {
        // Fill answers that are blank on this page from the other window;
        // where both have an answer, ask the student which version to keep.
        const next = createServerAnswers(error.details.answers, error.details.answerRevisions);
        const newConflicts = {};
        const adopted = {};
        questionIdsRef.current.forEach((questionId) => {
          const local = answerText(answersRef.current[questionId]);
          const serverText = serverEntry(next, questionId).text;
          if (local === serverText) return;
          if (local === "") adopted[questionId] = serverText;
          else newConflicts[questionId] = serverText;
        });
        setServerAnswersBoth(next);
        setConflictsBoth({ ...conflictsRef.current, ...newConflicts });
        if (Object.keys(adopted).length > 0) setAnswers((prev) => ({ ...prev, ...adopted }));
        setSubmitState({
          status: "error",
          message: "Some answers were changed in another window. Check them, then submit again.",
        });
      } else if (error?.code === "drawing-drafts-pending") {
        await drawingsRef.current.refresh();
        setSubmitState({ status: "drawing-drafts" });
      } else if (error?.code === "drawings-changed") {
        await drawingsRef.current.refresh();
        setSubmitState({
          status: "error",
          message: "A drawing was changed in another window. Check your drawings, then submit again.",
        });
      } else if (error?.code === "assignment-closed") {
        setServerDeadlineClosed(true);
        setSubmitState({ status: "idle" });
      } else if (error?.code === "refresh-required") {
        refreshRequiredRef.current = true;
        setRefreshRequired(true);
        setSubmitState({ status: "error", message: error.message });
      } else {
        await recoverSubmitOutcome(error);
      }
    } finally {
      submittingRef.current = false;
    }
  }, [flushSaves, finishAttempt, recoverSubmitOutcome]);

  const toggleTheme = () => {
    const nextMode = !isDarkMode;
    localStorage.setItem("darkMode", String(nextMode));
    setIsDarkMode(nextMode);
    document.body.classList.toggle("dark-mode", nextMode);
    document.documentElement.setAttribute("data-dark-mode", nextMode ? "true" : "false");
  };

  const handleLogout = async () => {
    try {
      await auth.signOut();
      navigate("/");
    } catch (logoutError) {
      console.error("Error signing out", logoutError);
    }
  };

  const renderEntryShell = (content) => (
    <div className={`sd-dashboard sd-brand-theme eq-portal-page${isDarkMode ? " sd-dark" : ""}`}>
      <DashboardSidebar
        role="student"
        username={displayStudentName}
        officialDashboardBrand
        onLogout={handleLogout}
        open={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        activePath="/enter-quiz"
      />
      <div className="sd-workspace eq-portal-workspace">
        <DashboardHeader
          username={displayStudentName}
          role="student"
          darkMode={isDarkMode}
          onToggleTheme={toggleTheme}
          onOpenMenu={() => setSidebarOpen(true)}
          search={portalSearch}
          onSearch={setPortalSearch}
        />
        <main className="sd-main eq-portal-main">
          <section className="eq-portal-hero">
            <div className="eq-portal-hero-copy">
              <p className="eq-portal-kicker">
                {assignmentId ? "Assigned assessment" : "Enter an assessment"}
              </p>
              <h1>{assignmentId ? "Preparing Assessment" : "Enter Test Code"}</h1>
              <p>
                {assignmentId
                  ? "We are securely checking your assignment and preparing your existing assessment session."
                  : "Enter the code provided by your teacher to access your assessment."}
              </p>
            </div>
            <div className="eq-portal-hero-art" aria-hidden="true">
              <span><KeyRound size={32} /></span>
              <i className="eq-portal-orbit eq-portal-orbit-one"><Sparkles size={17} /></i>
              <i className="eq-portal-orbit eq-portal-orbit-two"><ClipboardCheck size={18} /></i>
              <i className="eq-portal-orbit eq-portal-orbit-three"><BookOpenCheck size={17} /></i>
            </div>
          </section>
          {content}
        </main>
        <footer className="eq-portal-footer">© {new Date().getFullYear()} StudyDen. All rights reserved.</footer>
      </div>
    </div>
  );

  // ==================== Render ====================

  if (deadlineClosed) {
    return renderEntryShell(
      <section className="eq-portal-status-card" role="alert">
        <span className="eq-portal-status-icon eq-portal-status-warning"><AlertTriangle size={28} /></span>
        <p className="eq-portal-status-kicker">Assessment status</p>
        <h2>Closed</h2>
        <p>The submission deadline has passed. Answers saved before the deadline remain available for marking.</p>
        <button type="button" onClick={() => navigate(exitPath)}>
          <ChevronLeft size={16} />Back to {assignmentId ? "My Work" : "Dashboard"}
        </button>
      </section>
    );
  }

  // Already submitted screen
  if (alreadySubmitted) {
    return renderEntryShell(
      <section className="eq-portal-status-card" role="status">
        <span className="eq-portal-status-icon eq-portal-status-warning"><ClipboardCheck size={28} /></span>
        <p className="eq-portal-status-kicker">Assessment status</p>
        <h2>{terminalStatus !== "expired"
          ? "Already Submitted"
          : terminalExpiryReason === "assignment_deadline" ? "This work is closed" : "Time is up"}</h2>
        <p>{terminalStatus !== "expired"
          ? "You have already submitted this test."
          : terminalExpiryReason === "assignment_deadline"
            ? "The submission deadline passed. Your saved answers were submitted for marking."
            : "The assessment time ended. Your saved answers were submitted for marking."}</p>
        <p>Please contact your teacher if you believe this is an error.</p>
        <button type="button" onClick={() => navigate(exitPath)}>
          <ChevronLeft size={16} />Back to {assignmentId ? "My Work" : "Dashboard"}
        </button>
      </section>
    );
  }

  // Quiz locked screen
  if (quizLocked) {
    return renderEntryShell(
      <section className="eq-portal-status-card" role="alert">
        <span className="eq-portal-status-icon eq-portal-status-error"><AlertTriangle size={28} /></span>
        <p className="eq-portal-status-kicker">Assessment unavailable</p>
        <h2>This test is locked</h2>
        <p>Please contact your teacher.</p>
        <button type="button" onClick={() => navigate(exitPath)}>
          <ChevronLeft size={16} />Back to {assignmentId ? "My Work" : "Dashboard"}
        </button>
      </section>
    );
  }

  // Submission complete screen (the server confirmed the submission)
  if (submissionComplete) {
    return (
      <AssessmentOutcome
        kind="submitted"
        secondsRemaining={redirectCountdown}
        totalSeconds={OUTCOME_REDIRECT_SECONDS}
        destinationLabel={assignmentId ? "My Work" : "Dashboard"}
        onLeave={() => leaveOutcome(outcomeRedirect?.options)}
        darkMode={isDarkMode}
      />
    );
  }

  // Time up screen
  if (remainingTime === 0 && quizLoaded) {
    return (
      <AssessmentOutcome
        kind="time-up"
        status={expiryOutcome}
        secondsRemaining={redirectCountdown}
        totalSeconds={OUTCOME_REDIRECT_SECONDS}
        destinationLabel={assignmentId ? "My Work" : "Dashboard"}
        onLeave={() => leaveOutcome({ replace: true })}
        darkMode={isDarkMode}
      />
    );
  }

  if (!quizLoaded) {
    return renderEntryShell(
      <section className="eq-portal-entry-layout">
        <article className="eq-portal-form-card">
          <div className="eq-portal-card-heading">
            <span>{assignmentId ? <ShieldCheck size={22} /> : <KeyRound size={22} />}</span>
            <div>
              <p>{assignmentId ? "Secure assignment access" : "Test code access"}</p>
              <h2>{assignmentId ? "Preparing your assessment" : "Ready to begin?"}</h2>
            </div>
          </div>
          <p className="eq-portal-card-intro">
            {assignmentId
              ? "Your assignment details are being verified before the assessment opens."
              : "Select your teacher, then enter the test code exactly as it was provided."}
          </p>

          {assignmentId ? (
            <div className="eq-portal-assignment-state">
              {assignmentEntryLoading && (
                <div className="eq-portal-loading" role="status">
                  <LoaderCircle size={25} aria-hidden="true" />
                  <div><strong>Loading your assigned assessment...</strong><span>This should only take a moment.</span></div>
                </div>
              )}
              {!assignmentEntryLoading && accessError && (
                <div className="eq-portal-error" role="alert">
                  <AlertTriangle size={20} aria-hidden="true" /><span>{accessError}</span>
                </div>
              )}
              {!assignmentEntryLoading && !accessError && (
                <div className="eq-portal-loading" role="status">
                  <LoaderCircle size={25} aria-hidden="true" />
                  <div><strong>Preparing your assessment...</strong><span>Your existing session will be restored when available.</span></div>
                </div>
              )}
              {!assignmentEntryLoading && accessError && (
                <button type="button" className="eq-portal-primary-button" onClick={() => navigate("/my-assessments")}>
                  <ChevronLeft size={17} />Back to My Work
                </button>
              )}
            </div>
          ) : (
            <form
              className="eq-portal-form"
              aria-busy={isJoining}
              onSubmit={(event) => { event.preventDefault(); loadQuiz(); }}
            >
              <div className="eq-portal-field">
                <label htmlFor="teacherSelect"><UsersRound size={16} />Select Teacher</label>
                <p>Choose the teacher who assigned the test.</p>
                {teachersLoading ? (
                  <div className="eq-portal-loading eq-portal-loading-compact" role="status">
                    <LoaderCircle size={21} aria-hidden="true" /><strong>Loading your teachers...</strong>
                  </div>
                ) : linkedTeachers.length === 0 ? (
                  <div className="eq-portal-error" role="alert">
                    <AlertTriangle size={20} aria-hidden="true" />
                    <span>You haven't joined any teachers yet. <button type="button" onClick={() => navigate("/join-teacher")}>Join a Teacher</button> before taking a test.</span>
                  </div>
                ) : (
                  <div className="eq-portal-control-shell">
                    <UserCheck size={19} aria-hidden="true" />
                    <select
                      id="teacherSelect"
                      value={selectedTeacherId}
                      onChange={(event) => {
                        setSelectedTeacherId(event.target.value);
                        if (accessError) setAccessError(null);
                      }}
                    >
                      <option value="">— Select a teacher —</option>
                      {linkedTeachers.map((teacher) => (
                        <option key={teacher.uid} value={teacher.uid}>
                          {teacher.name || teacher.email || teacher.teacherCode}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>

              {linkedTeachers.length > 0 && (
                <div className="eq-portal-field">
                  <label htmlFor="quizCode"><KeyRound size={16} />Test Code</label>
                  <p>Enter the code exactly as it was given to you.</p>
                  <div className="eq-portal-control-shell eq-portal-code-shell">
                    <KeyRound size={19} aria-hidden="true" />
                    <input
                      id="quizCode"
                      type="text"
                      value={quizCode}
                      onChange={(event) => {
                        setQuizCode(event.target.value);
                        if (accessError) setAccessError(null);
                      }}
                      placeholder="e.g. ABC123"
                      disabled={!selectedTeacherId}
                    />
                  </div>
                </div>
              )}

              {accessError && (
                <div className="eq-portal-error" role="alert">
                  <AlertTriangle size={20} aria-hidden="true" /><span>{accessError}</span>
                </div>
              )}

              {linkedTeachers.length > 0 && (
                <button
                  type="submit"
                  className={`eq-portal-primary-button eq-load-btn${isJoining ? " is-joining" : ""}`}
                  disabled={!selectedTeacherId || isJoining}
                >
                  {isJoining ? (
                    <><LoaderCircle size={17} className="eq-load-btn-spinner" aria-hidden="true" />Joining…</>
                  ) : (
                    <><KeyRound size={17} aria-hidden="true" />Load Test</>
                  )}
                </button>
              )}
              <span className="eq-sr-only" role="status">
                {isJoining ? "Joining test, please wait." : ""}
              </span>
            </form>
          )}
        </article>

        <aside className="eq-portal-info-card">
          <span><BookOpenCheck size={23} /></span>
          <div>
            <p className="eq-portal-info-kicker">Helpful information</p>
            <h2>Where can I find my test code?</h2>
            <p>Your teacher will provide a test code when an assessment is ready for you.</p>
            <button type="button" onClick={() => navigate("/my-assessments")}>View assigned work</button>
          </div>
        </aside>
      </section>
    );
  }

  const saveStates = Object.fromEntries(quizQuestions.map((question) => [
    question.id,
    questionSaveState(question.id, {
      local: answers,
      server: serverAnswers,
      inFlight: inFlightSaves,
      failed: failedSaves,
      conflicts,
    }),
  ]));
  // Text, a selected option or a saved drawing makes a question answered; an
  // unsaved drawing draft does not.
  const answeredIds = new Set(quizQuestions
    .filter((question) => isAnswered(question, answers[question.id]) || drawings.hasDrawing(question.id))
    .map((question) => question.id));
  const drawingIds = new Set(quizQuestions.filter((question) => drawings.hasDrawing(question.id)).map((question) => question.id));
  const drawingPropsFor = (question) => {
    const summary = drawings.summaries[question.id] || null;
    if (question.questionType === "multipleChoice" ||
        !(drawings.available || summary?.hasDrawing || summary?.draft || summary?.editing)) return null;
    return {
      summary,
      busy: drawings.busy[question.id],
      notice: drawings.notices[question.id],
      padOpen: Boolean(drawings.pad),
      onOpen: () => drawings.openPad(question.id),
      onRemove: () => drawings.removeCommitted(question.id),
      onDiscardDraft: () => drawings.discardDraft(question.id),
      onDismissNotice: () => drawings.dismissNotice(question.id),
    };
  };
  const padQuestion = drawings.pad ? quizQuestions.find((question) => question.id === drawings.pad.questionId) : null;
  const pageSaveState = overallSaveState(Object.values(saveStates));
  const safeIndex = Math.min(currentIndex, Math.max(0, quizQuestions.length - 1));
  const currentQuestion = quizQuestions[safeIndex];
  const mediaAccess = assignmentId
    ? { type: "assignment", assignmentId }
    : { type: "quiz-code", teacherUid: resolvedTeacherUidRef.current, quizCode: resolvedQuizCodeRef.current };

  const handlePasteBlocked = (questionId) => {
    setPasteBlockedId(questionId);
    setTimeout(() => setPasteBlockedId((cur) => (cur === questionId ? null : cur)), 3000);
  };

  // Leaving through the breadcrumb first saves anything outstanding; the
  // attempt itself stays open on the server.
  const leaveAssessment = async () => {
    await flushSaves().catch(() => {});
    navigate(exitPath);
  };

  return (
    <div className={`eq-assess-page${isDarkMode ? " eq-assess-dark" : ""}`}>
      <header className="eq-assess-header">
        <div className="eq-assess-brand">
          <span className="eq-assess-logo"><img src={studyDenHorizontal} alt="StudyDen" /></span>
          <div className="eq-assess-title">
            <strong>{quizTitle || "Assessment"}</strong>
            <span>{quizQuestions.length} {quizQuestions.length === 1 ? "question" : "questions"}</span>
          </div>
        </div>
        <div className="eq-assess-header-meta">
          {remainingTime !== null && remainingTime > 0 && quizQuestions.length > 0 && (
            <div
              className={`eq-timer eq-assess-timer${remainingTime <= 60 ? " is-low" : ""}`}
              role="timer"
              aria-label={`Time remaining ${formatTime(remainingTime)}`}
            >
              <Clock size={19} aria-hidden="true" />
              <span className="eq-assess-timer-label">Time remaining</span>
              <strong>{formatTime(remainingTime)}</strong>
            </div>
          )}
          {remainingTime === null && quizQuestions.length > 0 && (
            <div className="eq-timer eq-assess-timer is-untimed">
              <TimerOff size={19} aria-hidden="true" />
              <strong>No time limit</strong>
            </div>
          )}
          <div className="eq-assess-student" aria-label={`Signed in as ${displayStudentName}`}>
            <span className="eq-assess-avatar" aria-hidden="true">{studentInitials(displayStudentName)}</span>
            <span className="eq-assess-student-name">{displayStudentName}</span>
          </div>
        </div>
      </header>

      <div className="eq-assess-body">
        <AssessmentNavigator
          questions={quizQuestions}
          currentIndex={safeIndex}
          answeredIds={answeredIds}
          saveStates={saveStates}
          reviewing={view === "review"}
          onSelect={goToQuestion}
          onReview={() => { focusQuestionRef.current = true; setView("review"); }}
        />

        <main className="eq-assess-main">
          <nav className="eq-assess-breadcrumb" aria-label="Breadcrumb">
            <button type="button" className="eq-assess-link-button" onClick={leaveAssessment}>
              {assignmentId ? "My assessments" : "Dashboard"}
            </button>
            <span aria-hidden="true">/</span>
            <span aria-current="page">{quizTitle || "Assessment"}</span>
          </nav>

          {resumedSession && (
            <div className="eq-resume-banner eq-assess-banner" role="status">
              We found your previous attempt. You can continue where you left off.
            </div>
          )}
          {refreshRequired && (
            <div className="eq-assess-banner is-error" role="alert">
              <span>This assessment was updated in another window. Refresh the page to continue — your saved answers will load.</span>
              <button type="button" className="eq-assess-secondary" onClick={() => window.location.reload()}>Refresh</button>
            </div>
          )}
          {saveError && !refreshRequired && pageSaveState === "failed" && (
            <div className="eq-assess-banner is-warning" role="alert">
              <span>{saveError}</span>
              <button type="button" className="eq-assess-secondary" onClick={() => runSave()}>Retry now</button>
            </div>
          )}

          <section className="eq-assess-card">
            {view === "review" ? (
              <AssessmentReview
                questions={quizQuestions}
                answeredIds={answeredIds}
                saveStates={saveStates}
                submitState={submitState}
                deadlineClosed={deadlineClosed}
                headingRef={questionHeadingRef}
                onSelect={goToQuestion}
                onBack={() => goToQuestion(safeIndex)}
                onSubmit={submitAssessment}
                drawingIds={drawingIds}
                draftIds={drawings.openDraftIds}
                drawingBusy={drawings.busy}
                onOpenDrawing={(index) => {
                  goToQuestion(index);
                  drawings.openPad(quizQuestions[index].id);
                }}
                onDiscardDraft={drawings.discardDraft}
              />
            ) : currentQuestion ? (
              <AssessmentQuestion
                key={currentQuestion.id}
                question={currentQuestion}
                index={safeIndex}
                total={quizQuestions.length}
                headingRef={questionHeadingRef}
                value={answers[currentQuestion.id] || ""}
                locked={Boolean(savedQuestions[currentQuestion.id])}
                overallSaveState={pageSaveState}
                manualSaving={manualSavingId === currentQuestion.id}
                conflictText={conflicts[currentQuestion.id]}
                allowPaste={allowPaste}
                pasteBlocked={pasteBlockedId === currentQuestion.id}
                quizId={resolvedQuizIdRef.current}
                mediaContext={{ type: "student", access: mediaAccess }}
                onChange={(value) => handleAnswerChange(currentQuestion.id, value)}
                onPasteBlocked={() => handlePasteBlocked(currentQuestion.id)}
                onSave={() => saveAnswer(currentQuestion.id)}
                onEdit={() => setSavedQuestions((prev) => ({ ...prev, [currentQuestion.id]: false }))}
                onRetrySave={() => runSave()}
                onResolveConflict={(keep) => resolveConflict(currentQuestion.id, keep)}
                onPrevious={() => goToQuestion(safeIndex - 1)}
                onNext={() => {
                  if (safeIndex === quizQuestions.length - 1) {
                    focusQuestionRef.current = true;
                    setView("review");
                  } else {
                    goToQuestion(safeIndex + 1);
                  }
                }}
                attemptId={sessionId}
                drawing={drawingPropsFor(currentQuestion)}
              />
            ) : (
              <div className="eq-empty">
                <p>No questions loaded yet.</p>
              </div>
            )}
          </section>

          <p className="eq-assess-footnote eq-autosave-notice">
            Your answers save automatically. You can also use Save answer.
            {remainingTime === null && " You can leave and come back later to finish."}
          </p>
        </main>
      </div>

      {drawings.pad && padQuestion && (
        <DrawingPad
          key={drawings.pad.editSessionId}
          pad={drawings.pad}
          question={padQuestion}
          quizId={resolvedQuizIdRef.current}
          mediaContext={{ type: "student", access: mediaAccess }}
          onChange={drawings.changeDrawing}
          onSave={drawings.saveDrawing}
          onCancel={drawings.cancelPad}
          onKeepEditing={drawings.keepEditing}
          onTakeOver={drawings.takeOver}
          onClose={drawings.closePad}
          onRetryOpen={() => drawings.openPad(drawings.pad.questionId)}
          onReplaceWithMine={drawings.replaceWithMine}
          onAdoptRecovered={drawings.adoptRecovered}
          onDiscardRecovered={drawings.discardRecovered}
        />
      )}
    </div>
  );
};

export default EnterQuiz;
